import React, { useState, useEffect } from 'react';
import { Paper, Typography, Stack, FormControl, InputLabel, Select, MenuItem, Box, Button } from '@mui/material';
import dayjs from 'dayjs';
import DateRangePicker from '../../../components/date-range-picker';
import { CallTrendChart } from '..';
import { useGetDailyCallTypeAnalytics } from '../../../../../../../features/analytics/hooks/useGetDailyCallTypeAnalytics';
import { CALL_TYPE_OPTIONS, DEFAULT_SELECTED_TYPE } from '../graph';
import { FileDownloadOutlined, RefreshOutlined } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { useExportDailyCallTypeAnalytics } from '../../../../../../../features/analytics/hooks/exports/useExportDailyCallTypeAnalytics';

const DailyCallType = ({ type, title, height = 380, fromDate: controlledFromDate, toDate: controlledToDate, onFromDateChange, onToDateChange }) => {
  const [localFromDate, setLocalFromDate] = useState(() => controlledFromDate || dayjs().startOf('month'));
  const [localToDate, setLocalToDate] = useState(() => controlledToDate || dayjs().endOf('month'));
  const [localType, setLocalType] = useState(type || DEFAULT_SELECTED_TYPE);

  useEffect(() => {
    if (controlledFromDate) {
      setLocalFromDate(controlledFromDate);
    }
  }, [controlledFromDate]);

  useEffect(() => {
    if (controlledToDate) {
      setLocalToDate(controlledToDate);
    }
  }, [controlledToDate]);

  const queryFrom = localFromDate.format('YYYY-MM-DD');
  const queryTo = localToDate.format('YYYY-MM-DD');

  useEffect(() => {
    if (type) setLocalType(type);
  }, [type]);

  const handleFromDateChange = (date) => {
    const nextDate = date || dayjs().startOf('month');
    setLocalFromDate(nextDate);
    if (onFromDateChange) {
      onFromDateChange(nextDate);
    }
  };

  const handleToDateChange = (date) => {
    const nextDate = date || dayjs().endOf('month');
    setLocalToDate(nextDate);
    if (onToDateChange) {
      onToDateChange(nextDate);
    }
  };

  const { data, isFetching, error, refetch } = useGetDailyCallTypeAnalytics({ from: queryFrom, to: queryTo, type: localType });

  const chartData = data && data.success && data.data && data.data.dailyBreakdown
    ? [
      { id: 'Answered', data: Object.keys(data.data.dailyBreakdown).sort().map(d => ({ x: d, y: data.data.dailyBreakdown[d].answered || 0 })) },
      { id: 'NotAnswered', data: Object.keys(data.data.dailyBreakdown).sort().map(d => ({ x: d, y: data.data.dailyBreakdown[d].notAnswered || 0 })) }
    ]
    : [];

  const exportHook = useExportDailyCallTypeAnalytics({
    onSuccess: (data) => {
      toast.success(`Daily call type analytics exported: ${data.filename}`);
    },
    onError: (err) => {
      toast.error(`Export failed: ${err.message}`);
    }
  });

  const handleExport = () => {
    const params = { from: queryFrom, to: queryTo, type: localType };
    toast.loading('Preparing export...', { id: 'daily-call-type-export' });
    exportHook.mutate(params, {
      onSettled: () => {
        toast.dismiss('daily-call-type-export');
      }
    });
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2, border: '1px solid #d0d0d0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <DateRangePicker
            fromDate={localFromDate}
            toDate={localToDate}
            onFromChange={handleFromDateChange}
            onToChange={handleToDateChange}
            disabled={isFetching || exportHook.isLoading}
            fromLabel="From"
            toLabel="To"
            size="small"
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="local-call-type-label">Call Type</InputLabel>
            <Select
              labelId="local-call-type-label"
              id="local-call-type-select"
              value={localType}
              label="Call Type"
              onChange={(e) => setLocalType(e.target.value)}
            >
              {CALL_TYPE_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={handleRefresh}
            disabled={isFetching || exportHook.isLoading}
            size="small"
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: '#f3f4f6',
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlined />}
            onClick={handleExport}
            disabled={isFetching || exportHook.isLoading || !chartData.length}
            size="small"
          >
            {exportHook.isLoading ? 'Exporting...' : 'Export'}
          </Button>
        </Stack>
      </Box>

      {isFetching ? (
        <Typography>Loading daily call type analytics...</Typography>
      ) : error ? (
        <Typography color="error">Error loading data: {error.message}</Typography>
      ) : (
        <CallTrendChart data={chartData} title={title} height={height} />
      )}
    </Paper>
  );
};

export default DailyCallType;
